import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useTable, useSortBy, useGlobalFilter } from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";

/* ================= GLOBAL SEARCH ================= */
const GlobalFilter = ({ globalFilter, setGlobalFilter }) => (
  <input
    className="form-control mb-3"
    placeholder="Search item..."
    value={globalFilter || ""}
    onChange={e => setGlobalFilter(e.target.value)}
  />
);

const LedgerProjectReport = () => {
  const token = localStorage.getItem("token");

  const [ledgerOptions, setLedgerOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);

  const [selectedLedger, setSelectedLedger] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ================= FETCH LEDGERS ================= */
  useEffect(() => {
    apiClient
      .get(`${SERVER_BASE}/ledgerMasters`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(res => {
        setLedgerOptions(
          res.data.map(l => ({
            value: l.printName,
            label: l.printName,
            ledgerId: l.id,
          }))
        );
      });
  }, []);

  /* ================= FETCH PROJECTS ================= */
  useEffect(() => {
    apiClient.get(`${SERVER_BASE}/area-projects`).then(res => {
      setProjectOptions(
        res.data.map(p => ({
          value: p.id,
          label: p.projectName,
        }))
      );
    });
  }, []);

  /* ================= GET REPORT ================= */
  const handleGetData = async () => {
    if (!selectedLedger || !selectedProjects.length) {
      alert("Select Ledger and Projects");
      return;
    }

    setLoading(true);
    const payload = {
      ledgerId: selectedLedger.ledgerId,
      projectIds: selectedProjects.map(p => p.value),
    };

    try {
      const res = await apiClient.post(
        `${SERVER_BASE}/ledger-project-report`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= TABLE CONFIG ================= */
  const columns = useMemo(
    () => [
      {
        Header: "Item",
        accessor: "itemName",
      },
      {
        Header: "Rec Qty",
        accessor: "receivedQty",
        className: "text-end",
      },
      {
        Header: "Return Qty",
        accessor: "issuedQty",
        className: "text-end",
      },
      {
        Header: "UOM",
        accessor: "uom",
      },
    ],
    []
  );

  const tableInstance = useTable(
    { columns, data },
    useGlobalFilter,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
  } = tableInstance;

  return (
    <div className="formcontainer mt-4">
      <h4 className="mb-3">Ledger Project Report</h4>

      {/* ================= FILTERS ================= */}
      <div className="row mb-3">
        <div className="col-md-4">
          <label className="form-label">Ledger</label>
          <Select
            options={ledgerOptions}
            value={selectedLedger}
            onChange={setSelectedLedger}
            placeholder="Select Ledger"
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Projects</label>
          <Select
            options={projectOptions}
            value={selectedProjects}
            onChange={setSelectedProjects}
            isMulti
            placeholder="Select Projects"
          />
        </div>

        <div className="col-md-2 d-flex align-items-end">
          <button
            className="btn btn-primary w-100"
            onClick={handleGetData}
            disabled={loading}
          >
            {loading ? "Loading..." : "Get Data"}
          </button>
        </div>
      </div>

      {/* ================= TABLE ================= */}
      {data.length > 0 && (
        <>
          <GlobalFilter
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
          />

          <div className="table-responsive">
            <table
              {...getTableProps()}
              className="table table-bordered table-sm"
            >
              <thead className="table-light">
                {headerGroups.map(hg => (
                  <tr {...hg.getHeaderGroupProps()}>
                    {hg.headers.map(column => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        style={{ cursor: "pointer" }}
                      >
                        {column.render("Header")}
                        {column.isSorted ? (
                          column.isSortedDesc ? " 🔽" : " 🔼"
                        ) : (
                          ""
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {rows.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => (
                        <td {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default LedgerProjectReport;
